/**
 * Core PDF Generator
 * Provides base PDF creation and common utilities
 */

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import {
  colors,
  fontSizes,
  page,
  hexToRgb,
  formatDateTime,
  documentTypes,
} from './pdfStyles';

/**
 * Anonymize a name by extracting initials
 * e.g., "Sarah Johnson" -> "SJ", "Sarah Maria Abel" -> "SMA"
 * @param {string} name - Full name to anonymize
 * @returns {string} - Initials
 */
export const anonymizeName = (name) => {
  if (!name) return 'N/A';
  return name
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

/**
 * Create a new PDF document with standard settings
 * @param {string} orientation - 'portrait' or 'landscape'
 * @returns {jsPDF} - Configured jsPDF instance
 */
export const createPDF = (orientation = 'portrait') => {
  const doc = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4',
  });

  // Set default font
  doc.setFont('helvetica');

  return doc;
};

/**
 * Add document header to PDF
 * @param {jsPDF} doc - jsPDF instance
 * @param {Object} options - Header options
 * @param {string} options.documentType - Type of document (from documentTypes)
 * @param {string} options.tenantName - Company/tenant name
 * @param {string} options.title - Optional custom title
 */
export const addHeader = (doc, { documentType, tenantName, title }) => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header background
  doc.setFillColor(...hexToRgb(colors.primary));
  doc.rect(0, 0, pageWidth, page.headerHeight, 'F');

  // Logo placeholder text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(fontSizes.subtitle);
  doc.setFont('helvetica', 'bold');
  doc.text('HR Case Management', page.marginLeft, 10);

  // Tenant name with "Organisation:" prefix
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'normal');
  doc.text(`Organisation: ${tenantName || 'Company Name'}`, page.marginLeft, 16);

  // Document type (right aligned)
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  const docTypeText = title || documentType || 'Document';
  const textWidth = doc.getTextWidth(docTypeText);
  doc.text(docTypeText, pageWidth - page.marginRight - textWidth, 13);

  // Reset text color
  doc.setTextColor(...hexToRgb(colors.text));

  return page.headerHeight + 5; // Return Y position after header
};

/**
 * Add document footer to PDF
 * @param {jsPDF} doc - jsPDF instance
 * @param {Object} options - Footer options
 * @param {string} options.exportedBy - User who exported
 * @param {Date} options.exportDate - Export timestamp
 * @param {number} options.pageNumber - Current page number
 * @param {number} options.totalPages - Total page count
 */
export const addFooter = (doc, { exportedBy, exportDate, pageNumber, totalPages }) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const footerY = pageHeight - page.footerHeight;

  // Footer line
  doc.setDrawColor(...hexToRgb(colors.border));
  doc.setLineWidth(0.5);
  doc.line(page.marginLeft, footerY, pageWidth - page.marginRight, footerY);

  // Footer text
  doc.setFontSize(fontSizes.tiny);
  doc.setTextColor(...hexToRgb(colors.textLight));

  // Left: Page number + Website stamp
  doc.text(`Page ${pageNumber} of ${totalPages}  |  www.hrcomp.app`, page.marginLeft, footerY + 5);

  // Center: Generated date
  const dateText = `Generated: ${formatDateTime(exportDate || new Date())}`;
  const dateWidth = doc.getTextWidth(dateText);
  doc.text(dateText, (pageWidth - dateWidth) / 2, footerY + 5);

  // Right: Exported by + Confidential
  const rightText = `Exported by: ${exportedBy || 'System'}`;
  const rightWidth = doc.getTextWidth(rightText);
  doc.text(rightText, pageWidth - page.marginRight - rightWidth, footerY + 5);

  // Confidential notice
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.danger));
  const confText = 'CONFIDENTIAL';
  const confWidth = doc.getTextWidth(confText);
  doc.text(confText, pageWidth - page.marginRight - confWidth, footerY + 10);

  // Reset
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(colors.text));
};

/**
 * Add footers to all pages
 * @param {jsPDF} doc - jsPDF instance
 * @param {Object} options - Footer options
 */
export const addFootersToAllPages = (doc, options) => {
  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, { ...options, pageNumber: i, totalPages });
  }
};

/**
 * Add a section title
 * @param {jsPDF} doc - jsPDF instance
 * @param {string} title - Section title
 * @param {number} y - Y position
 * @returns {number} - New Y position after title
 */
export const addSectionTitle = (doc, title, y) => {
  doc.setFontSize(fontSizes.heading);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.primary));
  doc.text(title.toUpperCase(), page.marginLeft, y);

  // Underline
  const textWidth = doc.getTextWidth(title.toUpperCase());
  doc.setDrawColor(...hexToRgb(colors.primary));
  doc.setLineWidth(0.5);
  doc.line(page.marginLeft, y + 1, page.marginLeft + textWidth, y + 1);

  // Reset
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(colors.text));

  return y + 8;
};

/**
 * Add a key-value pair line
 * @param {jsPDF} doc - jsPDF instance
 * @param {string} label - Label text
 * @param {string} value - Value text
 * @param {number} y - Y position
 * @param {Object} options - Additional options
 * @returns {number} - New Y position
 */
export const addKeyValue = (doc, label, value, y, options = {}) => {
  const { labelWidth = 40, valueColor = colors.text } = options;

  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text(`${label}:`, page.marginLeft, y);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(valueColor));
  doc.text(String(value || 'N/A'), page.marginLeft + labelWidth, y);

  doc.setTextColor(...hexToRgb(colors.text));

  return y + 5;
};

/**
 * Add a progress bar
 * @param {jsPDF} doc - jsPDF instance
 * @param {number} percentage - Progress percentage (0-100)
 * @param {number} y - Y position
 * @param {Object} options - Additional options
 * @returns {number} - New Y position
 */
export const addProgressBar = (doc, percentage, y, options = {}) => {
  const { width = 80, height = 4, x = page.marginLeft } = options;

  // Background
  doc.setFillColor(...hexToRgb(colors.border));
  doc.roundedRect(x, y, width, height, 1, 1, 'F');

  // Progress
  const progressWidth = (percentage / 100) * width;
  if (progressWidth > 0) {
    doc.setFillColor(...hexToRgb(colors.primary));
    doc.roundedRect(x, y, progressWidth, height, 1, 1, 'F');
  }

  // Percentage text
  doc.setFontSize(fontSizes.small);
  doc.setTextColor(...hexToRgb(colors.text));
  doc.text(`${Math.round(percentage)}%`, x + width + 3, y + 3);

  return y + height + 4;
};

/**
 * Add a status badge
 * @param {jsPDF} doc - jsPDF instance
 * @param {string} status - Status text
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {Object} statusColors - Color mapping for statuses
 */
export const addStatusBadge = (doc, status, x, y, statusColors) => {
  const statusLower = status?.toLowerCase() || 'unknown';
  const colorConfig = statusColors[statusLower] || { bg: '#f3f4f6', text: '#374151' };

  const text = status?.toUpperCase() || 'UNKNOWN';
  const textWidth = doc.getTextWidth(text);
  const padding = 2;
  const badgeWidth = textWidth + padding * 2;
  const badgeHeight = 5;

  // Badge background
  doc.setFillColor(...hexToRgb(colorConfig.bg));
  doc.roundedRect(x, y - 3.5, badgeWidth, badgeHeight, 1, 1, 'F');

  // Badge text
  doc.setFontSize(fontSizes.tiny);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colorConfig.text));
  doc.text(text, x + padding, y);

  // Reset
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(colors.text));

  return badgeWidth;
};

/**
 * Check if we need a new page and add one if necessary
 * @param {jsPDF} doc - jsPDF instance
 * @param {number} currentY - Current Y position
 * @param {number} requiredSpace - Space needed for next content
 * @param {Object} headerOptions - Options for header on new page
 * @returns {number} - New Y position (reset if new page added)
 */
export const checkPageBreak = (doc, currentY, requiredSpace, headerOptions) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxY = pageHeight - page.marginBottom - page.footerHeight;

  if (currentY + requiredSpace > maxY) {
    doc.addPage();
    if (headerOptions) {
      return addHeader(doc, headerOptions);
    }
    return page.marginTop;
  }

  return currentY;
};

/**
 * Add wrapped text (multi-line)
 * @param {jsPDF} doc - jsPDF instance
 * @param {string} text - Text to wrap
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} maxWidth - Maximum width before wrapping
 * @returns {number} - New Y position after text
 */
export const addWrappedText = (doc, text, x, y, maxWidth) => {
  if (!text) return y;

  doc.setFontSize(fontSizes.body);
  const lines = doc.splitTextToSize(String(text), maxWidth);
  doc.text(lines, x, y);

  return y + lines.length * 4;
};

/**
 * Generate filename for export
 * @param {string} prefix - File prefix (e.g., case ID)
 * @param {string} type - Export type
 * @returns {string} - Formatted filename
 */
export const generateFilename = (prefix, type) => {
  const date = new Date().toISOString().split('T')[0];
  const sanitizedPrefix = (prefix || 'export').replace(/[^a-zA-Z0-9-_]/g, '_');
  const typeMap = {
    case_summary: 'Summary',
    case_timeline: 'Timeline',
    staff_timeline: 'StaffTimeline',
    workflow: 'Workflow',
  };
  const typeName = typeMap[type] || 'Export';

  return `${sanitizedPrefix}_${typeName}_${date}.pdf`;
};

/**
 * Download the PDF
 * @param {jsPDF} doc - jsPDF instance
 * @param {string} filename - Filename for download
 */
export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};

/**
 * Get PDF as blob for preview
 * @param {jsPDF} doc - jsPDF instance
 * @returns {Blob} - PDF blob
 */
export const getPDFBlob = (doc) => {
  return doc.output('blob');
};

/**
 * Get PDF as data URL for preview
 * @param {jsPDF} doc - jsPDF instance
 * @returns {string} - Data URL
 */
export const getPDFDataUrl = (doc) => {
  return doc.output('dataurlstring');
};

/**
 * Add a Table of Contents
 * @param {jsPDF} doc - jsPDF instance
 * @param {Array} sections - Array of {title, page} objects
 * @param {number} y - Starting Y position
 * @param {Object} headerOptions - Header options for page breaks
 * @returns {number} - New Y position
 */
export const addTableOfContents = (doc, sections, y, headerOptions) => {
  y = addSectionTitle(doc, 'Table of Contents', y);

  const pageWidth = doc.internal.pageSize.getWidth();
  const dotWidth = doc.getTextWidth('.');

  sections.forEach((section, index) => {
    y = checkPageBreak(doc, y, 6, headerOptions);

    doc.setFontSize(fontSizes.body);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...hexToRgb(colors.text));

    // Section title
    const titleText = `${index + 1}. ${section.title}`;
    doc.text(titleText, page.marginLeft, y);

    // Page number (right aligned)
    const pageNumText = String(section.page);
    const pageNumWidth = doc.getTextWidth(pageNumText);
    const pageNumX = pageWidth - page.marginRight - pageNumWidth;
    doc.text(pageNumText, pageNumX, y);

    // Dotted line between title and page number
    const titleWidth = doc.getTextWidth(titleText);
    const dotsStartX = page.marginLeft + titleWidth + 2;
    const dotsEndX = pageNumX - 2;
    const numDots = Math.floor((dotsEndX - dotsStartX) / (dotWidth + 0.5));

    if (numDots > 0) {
      doc.setTextColor(...hexToRgb(colors.textLight));
      const dots = '.'.repeat(numDots);
      doc.text(dots, dotsStartX, y);
    }

    y += 5;
  });

  return y + 5;
};

/**
 * Draw a vertical timeline connector line
 * @param {jsPDF} doc - jsPDF instance
 * @param {number} x - X position for the line
 * @param {number} startY - Starting Y position
 * @param {number} endY - Ending Y position
 * @param {boolean} isFirst - Whether this is the first event (draw circle at top)
 * @param {boolean} isLast - Whether this is the last event (draw circle at bottom)
 */
export const drawTimelineConnector = (doc, x, startY, endY, isFirst = false, isLast = false) => {
  const circleRadius = 1.5;

  // Draw vertical line
  doc.setDrawColor(...hexToRgb(colors.primary));
  doc.setLineWidth(0.5);
  doc.line(x, startY, x, endY);

  // Draw circle at start point
  if (isFirst) {
    doc.setFillColor(...hexToRgb(colors.primary));
    doc.circle(x, startY, circleRadius, 'F');
  }

  // Draw circle at end point
  doc.setFillColor(...hexToRgb(colors.primary));
  doc.circle(x, endY, circleRadius, 'F');
};

/**
 * Draw a timeline event marker (bullet point)
 * @param {jsPDF} doc - jsPDF instance
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {string} type - Event type for color coding
 */
export const drawTimelineMarker = (doc, x, y, type = 'default') => {
  const markerColors = {
    action_completed: colors.success,
    stage_started: colors.primary,
    stage_completed: colors.primaryDark,
    document_uploaded: colors.secondary,
    note_added: colors.warning,
    case_created: colors.primary,
    default: colors.textLight,
  };

  const color = markerColors[type] || markerColors.default;
  doc.setFillColor(...hexToRgb(color));
  doc.circle(x, y, 2, 'F');
};

/**
 * Add an enhanced progress bar with label
 * @param {jsPDF} doc - jsPDF instance
 * @param {string} label - Label text
 * @param {number} completed - Number completed
 * @param {number} total - Total number
 * @param {number} y - Y position
 * @param {Object} options - Additional options
 * @returns {number} - New Y position
 */
export const addEnhancedProgressBar = (doc, label, completed, total, y, options = {}) => {
  const { width = 100, height = 6, showFraction = true } = options;
  const percentage = total > 0 ? (completed / total) * 100 : 0;

  // Label
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text(`${label}:`, page.marginLeft, y);

  const barX = page.marginLeft + 35;

  // Background bar
  doc.setFillColor(...hexToRgb(colors.border));
  doc.roundedRect(barX, y - 4, width, height, 2, 2, 'F');

  // Progress bar
  const progressWidth = (percentage / 100) * width;
  if (progressWidth > 0) {
    // Color based on percentage
    const progressColor = percentage >= 100 ? colors.success : percentage >= 50 ? colors.primary : colors.warning;
    doc.setFillColor(...hexToRgb(progressColor));
    doc.roundedRect(barX, y - 4, Math.max(progressWidth, 4), height, 2, 2, 'F');
  }

  // Percentage and fraction text
  doc.setFontSize(fontSizes.small);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.text));
  
  let statsText = `${Math.round(percentage)}%`;
  if (showFraction) {
    statsText += ` (${completed}/${total})`;
  }
  doc.text(statsText, barX + width + 5, y);

  return y + height + 4;
};

export { documentTypes };
